import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import { colors, fontFamily, optionStyle, selectSurface } from '../../theme';

const card = { background: colors.cardBg, boxShadow: colors.shadowCard, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, padding: '1.25rem', marginBottom: '1.25rem' };
const inputStyle = { width: '100%', padding: '0.6rem 0.75rem', background: colors.sectionBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 4, color: colors.textPrimary, boxSizing: 'border-box', fontFamily };
const btnPrimary = { padding: '0.6rem 1.25rem', background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 4, cursor: 'pointer', fontWeight: 600, fontFamily };
const btnDanger = { padding: '0.6rem 1.25rem', background: 'rgba(220,53,69,0.3)', border: `1px solid ${colors.negative}`, color: colors.negative, borderRadius: 4, cursor: 'pointer', fontWeight: 600, fontFamily };

const CustomerImport = () => {
  const [books, setBooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => { loadBooks(); }, []);

  const loadBooks = async () => {
    try {
      const data = await api.admin.getBooks();
      setBooks(data.books);
    } catch (err) {
      setError('Failed to load books');
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setPreview(null);
    setImportResult(null);
    setError(null);
    setSuccess(null);
  };

  const handlePreview = async () => {
    if (!file || !selectedBook) { setError('Please select a book and file'); return; }
    try {
      setLoading(true);
      setError(null);
      const data = await api.admin.previewImport(file, selectedBook);
      setPreview(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to preview import');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file || !selectedBook) { setError('Please select a book and file'); return; }
    if (!window.confirm('Are you sure you want to import these customers? This action cannot be undone.')) return;
    try {
      setLoading(true);
      setError(null);
      const data = await api.admin.executeImport(file, selectedBook);
      setImportResult(data);
      setSuccess('Import completed successfully');
      setPreview(null);
      setFile(null);
      document.getElementById('file-input').value = '';
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to import customers');
    } finally {
      setLoading(false);
    }
  };

  const th = { color: colors.textMuted, padding: '0.5rem', textAlign: 'left', fontWeight: 'normal', borderBottom: `1px solid ${colors.goldMuted}`, fontSize: '0.82rem' };
  const td = { color: colors.textPrimary, padding: '0.5rem', borderBottom: `1px solid ${colors.sectionBg}`, fontSize: '0.83rem' };

  return (
    <div style={{ fontFamily, color: colors.textPrimary }}>
      <h2 style={{ color: colors.textPrimary, marginBottom: '1.25rem', fontSize: '1.3rem' }}>Customer Import</h2>

      {error && <div style={{ background: 'rgba(220,53,69,0.1)', border: `1px solid ${colors.negative}`, color: colors.negative, borderRadius: 6, padding: '0.75rem', marginBottom: '1rem' }}>{error}</div>}
      {success && <div style={{ background: 'rgba(74,222,128,0.1)', border: `1px solid ${colors.odds}`, color: colors.odds, borderRadius: 6, padding: '0.75rem', marginBottom: '1rem' }}>{success}</div>}

      {/* Format info */}
      <div style={card}>
        <h4 style={{ color: colors.textPrimary, margin: '0 0 0.75rem 0', fontSize: '0.95rem' }}>Expected Excel Format</h4>
        <p style={{ color: colors.textMuted, fontSize: '0.82rem', marginBottom: '0.75rem' }}>Your Excel file should have the following columns:</p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr><th style={th}>Column</th><th style={th}>Required</th><th style={th}>Description</th></tr>
            </thead>
            <tbody>
              {[
                ['Customer', 'Yes', 'Username for login'],
                ['Password', 'Yes', 'Initial password'],
                ['Name', 'Yes', 'Display name'],
                ['Agent', 'No', 'Agent username (created if not exists)'],
                ['Credit Limit', 'No', 'Weekly betting limit'],
                ['Wager Limit', 'No', 'Max bet per wager'],
                ['P.Max Bet', 'No', 'Parlay max bet amount'],
                ['Balance', 'No', 'Initial balance adjustment'],
                ['FP Balance', 'No', 'Free play balance'],
                ['P.Max Pay', 'No', 'Parlay max payout'],
                ['Opening Date', 'No', 'Original account creation date'],
                ['Last Login', 'No', 'Last login date'],
              ].map(([col, req, desc]) => (
                <tr key={col}>
                  <td style={td}>{col}</td>
                  <td style={{ ...td, color: req === 'Yes' ? colors.odds : colors.textMuted }}>{req}</td>
                  <td style={{ ...td, color: colors.textMuted }}>{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload form */}
      <div style={card}>
        <div style={{ marginBottom: '0.85rem' }}>
          <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.4rem' }}>Select Book</label>
          <select value={selectedBook} onChange={e => setSelectedBook(e.target.value)} style={{ ...inputStyle, background: selectSurface }}>
            <option style={optionStyle} value="">-- Select Book --</option>
            {books.map(book => (
              <option style={optionStyle} key={book.id} value={book.id}>{book.name} ({book.username})</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: '0.85rem' }}>
          <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.4rem' }}>Excel File (.xlsx, .xls)</label>
          <input id="file-input" type="file" accept=".xlsx,.xls" onChange={handleFileChange} style={{ ...inputStyle, padding: '0.4rem' }} />
        </div>
        <button onClick={handlePreview} disabled={!file || !selectedBook || loading} style={{ ...btnPrimary, opacity: (!file || !selectedBook || loading) ? 0.5 : 1 }}>
          {loading ? 'Loading...' : 'Preview Import'}
        </button>
      </div>

      {/* Preview */}
      {preview && (
        <div style={card}>
          <h3 style={{ color: colors.textPrimary, margin: '0 0 1rem 0', fontSize: '1rem' }}>Import Preview</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
            {[
              { label: 'Total Rows', value: preview.total_rows, color: colors.textPrimary },
              { label: 'New Customers', value: preview.new_customers?.length || 0, color: colors.odds },
              { label: 'Will Skip', value: preview.existing_customers?.length || 0, color: colors.pending },
              { label: 'New Agents', value: preview.new_agents?.length || 0, color: colors.gold },
            ].map(s => (
              <div key={s.label} style={{ background: colors.sectionBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 6, padding: '0.75rem', textAlign: 'center' }}>
                <p style={{ color: colors.textMuted, fontSize: '0.75rem', margin: '0 0 0.35rem 0' }}>{s.label}</p>
                <p style={{ color: s.color, fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>{s.value}</p>
              </div>
            ))}
          </div>

          {preview.errors?.length > 0 && (
            <div style={{ background: 'rgba(220,53,69,0.1)', border: '1px solid rgba(220,53,69,0.3)', borderRadius: 6, padding: '0.75rem', marginBottom: '0.75rem' }}>
              <p style={{ color: colors.negative, fontWeight: 600, margin: '0 0 0.4rem 0', fontSize: '0.85rem' }}>Errors</p>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', color: colors.negative, fontSize: '0.82rem' }}>
                {preview.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}

          {preview.new_customers?.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ color: colors.gold, fontWeight: 600, fontSize: '0.85rem', margin: '0 0 0.5rem 0' }}>New Customers ({preview.new_customers.length})</p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead><tr><th style={th}>Row</th><th style={th}>Username</th><th style={th}>Name</th><th style={th}>Agent</th></tr></thead>
                  <tbody>
                    {preview.new_customers.slice(0, 20).map((c, i) => (
                      <tr key={i}><td style={td}>{c.row}</td><td style={td}>{c.username}</td><td style={td}>{c.name}</td><td style={td}>{c.agent}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.new_customers.length > 20 && <p style={{ color: colors.textMuted, fontSize: '0.8rem', marginTop: '0.5rem', fontStyle: 'italic' }}>...and {preview.new_customers.length - 20} more</p>}
            </div>
          )}

          <div style={{ textAlign: 'right', marginTop: '1rem' }}>
            <button onClick={handleImport} disabled={loading || (preview.new_customers?.length || 0) === 0} style={{ ...btnDanger, opacity: (loading || (preview.new_customers?.length || 0) === 0) ? 0.5 : 1 }}>
              {loading ? 'Importing...' : 'Execute Import'}
            </button>
          </div>
        </div>
      )}

      {/* Result */}
      {importResult && (
        <div style={card}>
          <h3 style={{ color: colors.textPrimary, margin: '0 0 1rem 0', fontSize: '1rem' }}>Import Complete</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
            {[
              { label: 'Customers Created', value: importResult.created_customers, color: colors.odds },
              { label: 'Customers Skipped', value: importResult.skipped_customers, color: colors.textMuted },
              { label: 'Agents Created', value: importResult.created_agents, color: colors.gold },
              { label: 'Balance Adjustments', value: importResult.balance_adjustments, color: colors.textMuted },
            ].map(s => (
              <div key={s.label} style={{ background: colors.sectionBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 6, padding: '0.75rem', textAlign: 'center' }}>
                <p style={{ color: colors.textMuted, fontSize: '0.75rem', margin: '0 0 0.35rem 0' }}>{s.label}</p>
                <p style={{ color: s.color, fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>{s.value}</p>
              </div>
            ))}
          </div>
          {importResult.errors?.length > 0 && (
            <div style={{ background: 'rgba(220,53,69,0.1)', border: '1px solid rgba(220,53,69,0.3)', borderRadius: 6, padding: '0.75rem' }}>
              <p style={{ color: colors.negative, fontWeight: 600, margin: '0 0 0.4rem 0', fontSize: '0.85rem' }}>Errors During Import</p>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', color: colors.negative, fontSize: '0.82rem' }}>
                {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerImport;
